import type { PipelineNode } from '@/modules/pipelines/api/entity-api';
import type { Notice } from '@/modules/pipelines/model/pipeline-alerts/pipeline-alert';
import { assetJoinKey } from '@/modules/pipelines/data/pipelines';

export interface PipelineMetricRow {
  id: string;
  name: string;
  segments: number;
  assets: number;
  notices: number;
  assetsWithNotices: number;
}

export interface AssetNoticeCount {
  id: string;
  name: string;
  pipelineName?: string;
  notices: number;
  planning: PlanningSplit;
}

export interface PlanningSplit {
  planned: number;
  unplanned: number;
  unknown: number;
}

export interface EventTypeAssetCount {
  eventType: string;
  assets: number;
  notices: number;
}

export interface PipelineMetrics {
  activeNotices: PlanningSplit;
  pipelines: PipelineMetricRow[];
  assetsByNoticeCount: AssetNoticeCount[];
  assetsByEventType: EventTypeAssetCount[];
  planning: PlanningSplit;
  totalNotices: number;
  assetsWithoutNotices: number;
}

function relatedEntityId(value: unknown): string | undefined {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object' && '_id' in value) {
    const id = (value as { _id?: unknown })._id;
    return typeof id === 'string' ? id : undefined;
  }
  return undefined;
}

export function derivePipelineMetrics(
  pipelines: PipelineNode[],
  notices: Notice[],
): PipelineMetrics {
  const assetName = new Map<string, string>();
  const assetPipelines = new Map<string, Set<string>>();
  const pipelineName = new Map<string, string>();
  const pipelineAssets = new Map<string, Set<string>>();
  const pipelineSegments = new Map<string, number>();

  for (const pipeline of pipelines) {
    const segments = pipeline.segments ?? [];
    pipelineName.set(pipeline._id, pipeline.name);
    pipelineSegments.set(pipeline._id, segments.length);

    const assets = pipelineAssets.get(pipeline._id) ?? new Set<string>();

    for (const region of pipeline.regions ?? []) {
      for (const zone of region.zones ?? []) {
        for (const asset of zone.assets ?? []) {
          const id = assetJoinKey(asset);
          assets.add(id);
          assetName.set(id, asset.name);

          const owners = assetPipelines.get(id) ?? new Set<string>();
          owners.add(pipeline._id);
          assetPipelines.set(id, owners);
        }
      }
    }

    pipelineAssets.set(pipeline._id, assets);
  }

  const noticesPerAsset = new Map<string, number>();
  const planningPerAsset = new Map<string, PlanningSplit>();
  const noticesPerPipeline = new Map<string, Set<string>>();
  const planning: PlanningSplit = { planned: 0, unplanned: 0, unknown: 0 };
  const activeNotices: PlanningSplit = { planned: 0, unplanned: 0, unknown: 0 };
  const assetsPerEventType = new Map<string, Set<string>>();
  const noticesPerEventType = new Map<string, number>();

  for (const notice of notices) {
    const bucket: keyof PlanningSplit =
      notice.planningStatus === 'planned'
        ? 'planned'
        : notice.planningStatus === 'unplanned'
          ? 'unplanned'
          : 'unknown';

    const noticeIsActive = notice.eventStatus === 'active';

    const eventType = notice.eventTypeClassification?.name || notice.eventType || 'Unknown';

    const seenAssets = new Set<string>();

    for (const raw of notice.relatedEntities ?? []) {
      const id = relatedEntityId(raw);
      if (!id || seenAssets.has(id) || !assetPipelines.has(id)) continue;
      seenAssets.add(id);

      noticesPerAsset.set(id, (noticesPerAsset.get(id) ?? 0) + 1);

      const typeAssets = assetsPerEventType.get(eventType) ?? new Set<string>();
      typeAssets.add(id);
      assetsPerEventType.set(eventType, typeAssets);

      const split =
        planningPerAsset.get(id) ?? ({ planned: 0, unplanned: 0, unknown: 0 } as PlanningSplit);
      split[bucket] += 1;
      planningPerAsset.set(id, split);

      for (const pipelineId of assetPipelines.get(id) ?? []) {
        const set = noticesPerPipeline.get(pipelineId) ?? new Set<string>();
        set.add(notice._id);
        noticesPerPipeline.set(pipelineId, set);
      }
    }

    if (seenAssets.size === 0) continue;

    planning[bucket] += 1;
    if (noticeIsActive) activeNotices[bucket] += 1;
    noticesPerEventType.set(eventType, (noticesPerEventType.get(eventType) ?? 0) + 1);
  }

  const rows: PipelineMetricRow[] = pipelines.map(pipeline => {
    const assets = pipelineAssets.get(pipeline._id) ?? new Set<string>();
    let assetsWithNotices = 0;
    assets.forEach(id => {
      if ((noticesPerAsset.get(id) ?? 0) > 0) assetsWithNotices += 1;
    });

    return {
      id: pipeline._id,
      name: pipeline.name,
      segments: pipelineSegments.get(pipeline._id) ?? 0,
      assets: assets.size,
      notices: noticesPerPipeline.get(pipeline._id)?.size ?? 0,
      assetsWithNotices,
    };
  });

  const assetsByNoticeCount: AssetNoticeCount[] = [...noticesPerAsset.entries()]
    .map(([id, count]) => {
      const owners = [...(assetPipelines.get(id) ?? [])];
      return {
        id,
        name: assetName.get(id) ?? id,
        pipelineName: owners.length === 1 ? pipelineName.get(owners[0]) : undefined,
        notices: count,
        planning: planningPerAsset.get(id) ?? { planned: 0, unplanned: 0, unknown: 0 },
      };
    })
    .sort((a, b) => b.notices - a.notices || a.name.localeCompare(b.name));

  const assetsByEventType: EventTypeAssetCount[] = [...assetsPerEventType.entries()]
    .map(([eventType, assets]) => ({
      eventType,
      assets: assets.size,
      notices: noticesPerEventType.get(eventType) ?? 0,
    }))
    .sort(
      (a, b) =>
        b.notices - a.notices || b.assets - a.assets || a.eventType.localeCompare(b.eventType),
    );

  return {
    activeNotices,
    assetsByEventType,
    pipelines: rows,
    assetsByNoticeCount,
    planning,
    totalNotices: notices.length,
    assetsWithoutNotices: assetPipelines.size - noticesPerAsset.size,
  };
}
